import Constants from 'expo-constants';
import i18n from '@/lib/i18n';
import { recordHandledError } from '@/lib/crashReporting';

/**
 * Login Google + access token pro Drive (escopo `drive.file`). Ver ADR 0027.
 *
 * O módulo nativo `@react-native-google-signin/google-signin` é carregado por lazy-require
 * — mesmo padrão do `expo-document-picker` em backupService.ts. Assim o app compila e roda
 * SEM ele; só o fluxo de Drive exige o dev build novo + o setup no Google Console
 * (ver docs/BACKUP_GOOGLE_DRIVE_SETUP.md). Sem o módulo/config, as funções lançam um erro
 * amigável que a UI transforma em "atualize o app / reconecte".
 */

const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

const EXTRA = Constants.expoConfig?.extra as Record<string, unknown> | undefined;

/** webClientId do OAuth "Web client" do Console — não é segredo (client IDs são públicos). */
const WEB_CLIENT_ID = EXTRA?.googleWebClientId as string | undefined;

/**
 * iOS clientID (derivado do URL scheme no app.config.js). O iOS EXIGE isto no
 * GIDConfiguration — sem ele o signIn lança "You must specify |clientID|". No Android
 * é ignorado pelo configure(), então passar sempre é seguro.
 */
const IOS_CLIENT_ID = EXTRA?.googleIosClientId as string | undefined;

/**
 * O que a pessoa lê no card "O backup parou" quando este binário não tem como falar com o Drive.
 * Já foi "falta googleWebClientId. Ver docs/…": o iOS 1.0.74 saiu sem o client id (24/10/2026) e
 * isso apareceu para usuário. O detalhe técnico vai pro Sentry e, em dev, pro console.
 */
function driveNeedsUpdateError(): Error {
    return new Error(i18n.t('settings:backup.errors.driveNeedsUpdate'));
}

let reportedMissingConfig = false;

interface GoogleUser {
    user?: { email?: string | null };
}

/** v13+ do google-signin embrulha signIn/signInSilently em { type, data } (antes era plano). */
interface SignInResponse {
    type: string; // 'success' | 'cancelled' | 'noSavedCredentialFound'
    data: GoogleUser | null;
}

interface GoogleSignInModule {
    GoogleSignin: {
        configure: (opts: Record<string, unknown>) => void;
        hasPlayServices: (opts?: { showPlayServicesUpdateDialog?: boolean }) => Promise<boolean>;
        signIn: () => Promise<SignInResponse>;
        signInSilently: () => Promise<SignInResponse>;
        getTokens: () => Promise<{ accessToken: string; idToken: string | null }>;
        getCurrentUser: () => GoogleUser | null;
        signOut: () => Promise<unknown>;
    };
}

let configured = false;

/**
 * Detecta o módulo nativo SEM disparar o `getEnforcing` do google-signin.
 *
 * O `require` do pacote roda, no topo, `TurboModuleRegistry.getEnforcing('RNGoogleSignin')`,
 * que LANÇA se o módulo nativo não está no binário — e o RN reporta esse throw ao LogBox
 * (redbox em dev) ANTES do nosso try/catch pegá-lo. Então antes de requerer, sondamos com o
 * `get` NÃO-enforcing (devolve null em vez de lançar). Assim, sem o dev build, a tela degrada
 * de verdade ("Disponível após atualizar o app"), sem redbox.
 */
/**
 * Carrega o módulo nativo do google-signin.
 *
 * O `require` dispara, no topo do pacote, o `getEnforcing('RNGoogleSignin')` dele: COM o módulo
 * no binário (após o dev build) resolve normal e devolve o módulo; SEM ele (janela dev: pacote no
 * JS mas nativo ainda não buildado) lança — pegamos no catch e devolvemos null, e a UI cai no
 * "Disponível após atualizar o app". Em produção não há LogBox, então é degradação limpa; em dev
 * o RN pinta um redbox dismissível nessa janela transitória (aceitável — é o lembrete de rebuildar).
 *
 * NÃO trocar por `TurboModuleRegistry.get('RNGoogleSignin')`: dá falso-negativo (o módulo existe,
 * mas o registro só resolve pelo `require` do pacote) e esconde a feature mesmo instalada.
 */
function loadModule(): GoogleSignInModule | null {
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        return require('@react-native-google-signin/google-signin') as GoogleSignInModule;
    } catch {
        return null;
    }
}

/** true se o módulo nativo está presente (dev build feito). */
export function isGoogleDriveAvailable(): boolean {
    return loadModule() != null;
}

function requireModule(): GoogleSignInModule['GoogleSignin'] {
    const mod = loadModule();
    if (!mod) {
        if (__DEV__) console.warn('[googleAuth] google-signin nativo ausente: faça um novo build (npx expo run:android / run:ios).');
        throw driveNeedsUpdateError();
    }
    if (!WEB_CLIENT_ID) {
        if (!reportedMissingConfig) {
            // Binário de loja sem o client id: só o usuário via o erro, e o suporte por tabela.
            reportedMissingConfig = true;
            recordHandledError(new Error('Google Drive: build without extra.googleWebClientId'), {
                kontto_op: 'drive_config_missing',
            });
        }
        if (__DEV__) console.warn('[googleAuth] sem extra.googleWebClientId: ver docs/BACKUP_GOOGLE_DRIVE_SETUP.md.');
        throw driveNeedsUpdateError();
    }
    if (!configured) {
        mod.GoogleSignin.configure({
            webClientId: WEB_CLIENT_ID,
            iosClientId: IOS_CLIENT_ID,
            scopes: [DRIVE_FILE_SCOPE],
            // SEM offlineAccess: não temos backend. O access token pro Drive REST vem do
            // getTokens() (a lib renova sozinha, client-side); offlineAccess só pediria um
            // serverAuthCode que não usamos — e é um vetor comum de DEVELOPER_ERROR.
        });
        configured = true;
    }
    return mod.GoogleSignin;
}

/** Login interativo pedindo o escopo drive.file. Retorna o e-mail conectado. */
export async function connectGoogleDrive(): Promise<string> {
    const GoogleSignin = requireModule();
    await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
    const info = await GoogleSignin.signIn();
    // v13+ embrulha em { type: 'success', data: { user } } — não é mais info.user direto.
    const email = info.data?.user?.email;
    if (!email) throw new Error('Não foi possível ler a conta Google.');
    return email;
}

/** Access token pro Drive (a lib renova sozinha). Lança se não houver sessão. */
export async function getAccessToken(): Promise<string> {
    const GoogleSignin = requireModule();
    try {
        const { accessToken } = await GoogleSignin.getTokens();
        return accessToken;
    } catch {
        // iOS: depois de um cold start, getTokens() rejeita ("requires a user to be
        // signed in") porque o `currentUser` VIVE EM MEMÓRIA e é nil até a sessão ser
        // reidratada do keychain. signInSilently() faz esse restore. No Android é inócuo
        // (a sessão é persistente e getTokens já funciona). Sem isso, o backup automático
        // no iOS falhava calado em toda reabertura (ver code-review / ADR 0027).
        await GoogleSignin.signInSilently();
        const { accessToken } = await GoogleSignin.getTokens();
        return accessToken;
    }
}

/** E-mail da conta atualmente conectada, sem pedir login (null se não houver). */
export function getConnectedEmail(): string | null {
    const mod = loadModule();
    if (!mod || !WEB_CLIENT_ID) return null;
    try {
        return mod.GoogleSignin.getCurrentUser()?.user?.email ?? null;
    } catch {
        return null;
    }
}

/** Tenta reidratar a sessão sem UI (ex.: no boot, antes do backup automático). */
export async function restoreGoogleSession(): Promise<string | null> {
    try {
        const GoogleSignin = requireModule();
        const info = await GoogleSignin.signInSilently();
        return info.data?.user?.email ?? null;
    } catch {
        return null;
    }
}

export async function disconnectGoogleDrive(): Promise<void> {
    const mod = loadModule();
    if (!mod) return;
    try {
        await mod.GoogleSignin.signOut();
    } catch {
        // se já estava deslogado, tudo bem
    }
}
